import { useEffect } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getPageElementCounts } from '@atlaskit/editor-common/utils/page-element-counts';

/**
 * Fires the single `rendererInitialised` operational event for a legacy content macro renderer.
 *
 * Both entry points into the nested renderer need this: the client renderer that the extension
 * manifest mounts, and the hydration enhancer that attaches client-only providers to a renderer
 * already mounted from server markup. Exactly one of them runs for any given LCM node, so the
 * event fires once either way.
 */
export const useLegacyContentMacroRendererAnalytics = ({
	adfDocument,
	contentId,
}: {
	adfDocument?: DocNode;
	contentId?: string;
}): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!contentId || !createAnalyticsEvent || !adfDocument) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'rendererInitialised',
				actionSubject: 'legacyContentMacro',
				source: 'legacyContentMacro',
				attributes: {
					objectId: contentId,
					pageElementCounts: getPageElementCounts(adfDocument),
				},
			},
		}).fire();
	}, [adfDocument, contentId, createAnalyticsEvent]);
};
